import React, { useState, useEffect } from 'react';
import api from './api/axios';
import { v4 as uuidv4 } from 'uuid';
import useArgument from './useArgument';
import ArgumentView from './components/ArgumentView';
import PageContainer from './components/PageContainer';
import ShareArgumentLinkDialog from './components/ShareArgumentLinkDialog';

import SendIcon from '@mui/icons-material/Send';
import {
  Box,
  Typography,
  TextField,
  Button,
} from '@mui/material';

const HomePage: React.FC = () => {
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [uid, setUid] = useState('');
  const [userName, setUserName] = useState('');
  const [topic, setTopic] = useState('');
  const [showArgumentLink, setShowArgumentLink] = useState(false);
  const [stage, setStage] = useState<'initial' | 'argument'>('initial');
  const [showShareDialog, setShowShareDialog] = useState(false);

  const argumentHook = useArgument({ uid, userName });

  // Helper to encode URIs
  const encode = (value: string) => encodeURIComponent(value.trim());

  // Create argument on server
  const handleCreateArgument = async () => {
    if (!userName.trim() || !topic.trim()) {
      setMessage('Please enter your name and a topic.');
      return;
    }

    const newUid = uuidv4();
    setLoading(true);
    setMessage('');

    try {
      await api.post(
        `/create-argument/${newUid}/${encode(topic)}/`,
        {}
      );
      setUid(newUid);
      setShowArgumentLink(true);
    } catch (error) {
      console.error('Error creating argument:', error);
      setMessage('Failed to create argument. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Close the link dialog and move to argument stage
  const handleCloseArgumentLink = () => {
    setShowArgumentLink(false);
    setStage('argument');
  };

  // Copy generated link to clipboard
  const handleCopyLink = () => {
    const link = shareLinkUrl();
    navigator.clipboard.writeText(link);
  };

  // Helper to build the share link
  const shareLinkUrl = () =>
    `${window.location.origin}/add-side-of-argument/${uid}/${encode(userName)}/${encode(topic)}/`;

  // Construct a dynamic subtitle
  const subtitle =
    stage === 'initial'
      ? 'Start Your Argument'
      : argumentHook.opponentName
      ? `Arguing with ${argumentHook.opponentName} about ${topic}`
      : `Arguing about ${topic}`;

  useEffect(() => {
    console.log('[HomePage] Current state:', {
      uid,
      userName,
      isFinalized: argumentHook.isFinalized,
      hasJudgment: !!argumentHook.judgment,
      opponentState: argumentHook.opponentState
    });
  }, [uid, userName, argumentHook.isFinalized, argumentHook.judgment, argumentHook.opponentState]);

  return (
    <PageContainer subtitle={subtitle}>
      <Typography variant="subtitle1">
        Quick, fair resolutions to any disagreement.
      </Typography>

      <ShareArgumentLinkDialog
        open={showArgumentLink}
        onClose={handleCloseArgumentLink}
        linkUrl={shareLinkUrl()}
        handleCopyLink={handleCopyLink}
      />

      {/* Initial Stage: ask for name & topic */}
      {stage === 'initial' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, mt: 4 }}>
          <TextField
            label="Your Name"
            value={userName}
            onChange={(e) => setUserName(e.target.value)}
            variant="outlined"
            fullWidth
            sx={{ maxWidth: 600 }}
          />
          <TextField
            label="Argument Topic"
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            variant="outlined"
            fullWidth
            sx={{ maxWidth: 600 }}
          />
          <Button
            onClick={handleCreateArgument}
            variant="contained"
            color="primary"
            disabled={loading}
          >
            {loading ? 'Creating...' : 'Start Argument'}
          </Button>
        </Box>
      )}

      {/* Argument Stage*/}
      {stage === 'argument' && (
        <>
          <Box sx={{ mt: 4 }}>
            {/* Share text link - only show if no judgment yet */}
            {!argumentHook.judgment && (
              <Typography 
                sx={{ 
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1,
                  mb: 1
                }}
              >
                Invite your opponent if you haven't already. {' '}
                <Box
                  component="span"
                  onClick={() => setShowShareDialog(true)}
                  sx={{
                    color: 'primary.main',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    cursor: 'pointer',
                    '&:hover': {
                      textDecoration: 'underline',
                    },
                  }}
                >
                  Share argument
                  <SendIcon fontSize="small" />
                </Box>
              </Typography>
            )}
          </Box>
          <ArgumentView
            argumentHook={argumentHook}
            opponentName={argumentHook.opponentName}
            opponentState={argumentHook.opponentState}
            topic={topic}
          />
        </>
      )}

      {/* Error or status messages */}
      {message && (
        <Typography color="error" sx={{ mt: 2 }}>
          {message}
        </Typography>
      )}

      {/* Share Dialog */}
      <ShareArgumentLinkDialog
        open={showShareDialog}
        onClose={() => setShowShareDialog(false)}
        linkUrl={shareLinkUrl()}
        handleCopyLink={handleCopyLink}
      />
    </PageContainer>
  );
};

export default HomePage;
